import { useQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import type { ChartData } from 'chart.js';
import { type MouseEvent, useMemo, useState } from 'react';

import { CenterFlex, Flex, VFlex } from '@/components/base';
import type { LegendProps } from '@/components/Chart/LegendItem/LegendItem';
import Spinner from '@/components/Common/Spinner';
import GridTopList from '@/components/GridList/GridTopList';
import queryKeys from '@/configs/react-query-keys';
import useAgentStore from '@/features/Agent/stores';
import { CollapseState } from '@/features/AskSyncly/types/constants';
import type { FeedbackBlockListModalOptions } from '@/features/Feedback/components/FeedbackListModal/types';
import { useSearchDetailContext } from '@/features/GlobalSearch/context/SearchDetailContext';
import useGlobalSearchModalAction from '@/features/GlobalSearch/hooks/useGlobalSearchModalAction';
import { useAgentFilterMap } from '@/features/StructuredFilter/configs/useAgentFilterMap';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useColor from '@/hooks/domain/useColor';
import useModalState from '@/hooks/pure/useModalState';
import service from '@/services/index';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { chartColors } from '@/styles/colors/chartColors';
import { AGENT_COLUMNS } from '@/types/filterKeyConst';
import { GPT_DOMAIN } from '@/types/gptConst';

import DistributionChart from '../../DistributionChart/DistributionChart';
import LegendList from '../../DistributionChart/LegendList';
import type { DISTRIBUTION_CHART_LEGEND } from '../../DistributionChart/types';
import SearchContentStyles from '../SearchContentStyles';
import SearchDrilldownMenu from '../SearchDrilldownMenu';

const { ContentBox, ContentHeader, DownloadButton } = SearchContentStyles;

const AgentDistributionContainer = () => {
  const { alias, searchResult, filterQueryFormats } = useSearchDetailContext();
  const { addToFilterSingleValue } = useGlobalSearchModalAction();
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const agentMap = useAgentStore((state) => state.agentMap);
  const color = useColor();
  const fieldList = [AGENT_COLUMNS.ROLE, AGENT_COLUMNS.TEAM];
  const filterMap = useAgentFilterMap();
  const filterList = fieldList.map((field) => {
    const target = filterMap[field];
    if (!target) throw new Error(`${field} Target not found`);
    return {
      value: field,
      label: target.columnName,
    };
  });

  const { status, open, close, pos, setPos, modalState, setModalState } =
    useModalState<{
      segment: {
        value: StringNumberValue;
        name: string;
      };
      columnKey: string;
    } | null>(null);

  const [selectedAttrValue, setSelectedAttrValue] = useState<
    string | DISTRIBUTION_CHART_LEGEND | null
  >(null);

  const [field, setField] = useState<string>(AGENT_COLUMNS.ROLE);
  const { data, isLoading } = useQuery({
    queryFn: () => service.globalSearch.fetchAgentDist(alias),
    queryKey: [
      queryKeys.search.SEARCH_DETAIL,
      queryKeys.search.SEARCH_RESULT_AGENTS,
      alias,
    ],
  });

  const chartTrendData: {
    chart: ChartData<'doughnut'>;
    legend: LegendProps[];
    assignedSum: number;
    notAssigned: number;
    total: number;
  } | null = useMemo(() => {
    if (!data) return null;
    const statistics =
      field === AGENT_COLUMNS.ROLE ? data.role_dist : data.team_dist;

    const sum = statistics.buckets.reduce((acc, t) => acc + t.count, 0) || 0;
    const notAssigned = statistics.total - sum;

    return {
      assignedSum: sum,
      notAssigned: notAssigned,
      legend: statistics.buckets.map((d, i) => ({
        name: d.bucket as string,
        value: d.count,
        color: chartColors[i],
        percentage: (d.count / statistics.total) * 100,
      })),
      total: statistics.total,
      chart: {
        labels: [...statistics.buckets.map((t) => t.bucket), 'Not assigned'],
        datasets: [
          {
            data: [...statistics.buckets.map((t) => t.count), notAssigned],
            backgroundColor: [
              ...statistics.buckets.map((t, index) => {
                if (t.bucket === 'Others') return color.gray400;
                return chartColors[index];
              }),
              color.gray300,
            ],
            borderColor: color.gray100,
          },
        ],
      },
    };
  }, [color.gray100, color.gray300, color.gray400, data, field]);

  const onViewFeedbackListHandler = async (
    e: MouseEvent,
    initialOpen?: boolean,
  ) => {
    e.stopPropagation();
    close();
    if (!modalState) return;
    const segment = modalState.segment;

    const filterPayload = {
      [modalState.columnKey]: structuredFilterUtils.buildSingleValue(
        segment.value,
      ),
    };

    setNewModal<FeedbackBlockListModalOptions>({
      alias,
      type: DETAIL_MODAL_TYPE.FEEDBACK_BLOCK_LIST,
      idx: 0,
      keys: [field, segment.value.toString()],
      options: {
        queryKeys: [field, segment.value.toString()],
        title: 'Feedback list',
        subTitle: `Agent ${filterMap[modalState.columnKey].columnName} : ${
          segment.name
        }`,
        domain: 'global-search',
        fetcher: async (
          sortStatus: NotNullableSortStatusT,
          pageParam?: null | string,
        ) => {
          const res = await service.globalSearch.fetchSearchFeedbackList(
            alias,
            {
              query: {
                cursor: pageParam,
                sortby: sortStatus?.sortBy,
                order: sortStatus?.orderStatus,
              },
              payload: filterPayload,
            },
          );

          const PAGE_SIZE = 50;

          return {
            count: res.count,
            list: res.feedbacks,
            paginate_token: res.feedbacks[PAGE_SIZE]?.alias,
          };
        },
        exportFetcher: async (reason?: string, password?: string) => {
          await service.globalSearch.exportSearchFeedbackList({
            alias: searchResult.alias,
            filterPayload: filterPayload,
            reason: reason,
            password: password,
          });
        },
        socketInfo: {
          domain: GPT_DOMAIN.GLOBAL_SEARCH,
          search: {
            alias: searchResult.alias,
            filterPayload: filterPayload,
          },
          initialState: initialOpen ? CollapseState.EXPANDED : undefined,
        },
      },
    });
  };

  const onAddToFilterHandler = async (e: MouseEvent) => {
    e.stopPropagation();
    close();
    if (!modalState) return;
    await addToFilterSingleValue(searchResult, filterQueryFormats, {
      segment: modalState.segment,
      columnKey: modalState.columnKey,
    });
  };

  return (
    <VFlex g={24}>
      <ContentBox padding="24px" g={24}>
        <Flex j="space-between" a="center">
          <ContentHeader>Related agents</ContentHeader>
          <DownloadButton onClick={() => {}} />
        </Flex>
        <GridTopList
          data={data?.list
            .filter((agent) => {
              const agentInfo = agentMap[agent.alias];
              if (!agentInfo) return false;
              return true;
            })
            .map((agent) => {
              const agentInfo = agentMap[agent.alias];
              return {
                bucket: agent.alias,
                name: agentInfo.name,
                count: agent.count,
              };
            })}
          isLoading={isLoading}
          onClickItem={(e, bucket) => {
            setPos({
              top: e.clientY,
              left: e.clientX,
            });
            setModalState({
              segment: {
                value: bucket.bucket.toString(),
                name: bucket.name || '',
              },
              columnKey: AGENT_COLUMNS.ALIAS,
            });
            open();
          }}
        />
      </ContentBox>
      <ContentBox padding="24px" g={24}>
        <Flex j="space-between" a="center">
          <Flex a="center" g={8}>
            <ContentHeader>Agent distribution</ContentHeader>
            <Select
              style={{ width: 120 }}
              size="small"
              value={field}
              options={filterList}
              onChange={setField}
            />
          </Flex>
          <DownloadButton onClick={() => {}} />
        </Flex>
        {!chartTrendData || isLoading ? (
          <CenterFlex style={{ height: 250, width: '100%' }}>
            <Spinner />
          </CenterFlex>
        ) : (
          <Flex g={64} a="flex-start">
            <DistributionChart
              dataLength={chartTrendData.legend.length}
              chartData={chartTrendData.chart}
              total={chartTrendData.total}
              label="Feedback count"
            />
            <LegendList
              legend={chartTrendData.legend}
              assignedSum={chartTrendData.assignedSum}
              notAssigned={chartTrendData.notAssigned}
              total={chartTrendData.total}
              selectedLegend={selectedAttrValue}
              onClickLegendItem={(e, value) => {
                if (typeof value !== 'string') return;
                setSelectedAttrValue(value);
                setPos({
                  top: e.clientY,
                  left: e.clientX,
                });
                setModalState({
                  segment: {
                    value: value,
                    name: value,
                  },
                  columnKey: field,
                });
                open();
              }}
            />
          </Flex>
        )}
      </ContentBox>
      <SearchDrilldownMenu
        pos={{
          top: pos?.top as number,
          left: pos?.left as number,
        }}
        status={status}
        close={close}
        onViewFeedbackListClick={onViewFeedbackListHandler}
        onAddToFilterClick={onAddToFilterHandler}
        onAskSynclyAIClick={(e) => onViewFeedbackListHandler(e, true)}
      />
    </VFlex>
  );
};

export default AgentDistributionContainer;
